import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppActionMenu from '../../../../components/AppActionMenu';

export default function PayHeadsTable({
  records = [],
  isLoading = false,
  onDelete,
  onArchive,
  onResetFilters,
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-16 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Pay Heads Master...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-16 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <span className="material-symbols-outlined text-4xl text-on-surface-variant/40">paid</span>
          <p className="text-sm font-bold text-on-surface">No Pay Heads found</p>
          <p className="text-xs text-on-surface-variant max-w-sm">
            No pay head master records match your search criteria. Create one using the button above.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[750px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Pay Head Name</th>
              <th className="w-36 px-4 py-3.5">Type</th>
              <th className="w-48 px-4 py-3.5">Calculation Mode</th>
              <th className="w-40 px-4 py-3.5">Rules &amp; Slabs</th>
              <th className="w-28 px-4 py-3.5 text-center">Payslip</th>
              <th className="w-28 px-4 py-3.5 text-center">Status</th>
              <th className="w-16 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => (
              <tr
                key={item.id}
                onClick={() => navigate(`/payroll/payheads/entry/${item.id}`)}
                className="transition-colors hover:bg-surface-container-low/50 cursor-pointer group"
              >
                {/* Name & Alias */}
                <td className="px-5 py-3.5 font-bold text-on-surface">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-on-surface group-hover:text-primary transition-colors">
                      {item.name}
                    </span>
                    {item.alias && (
                      <span className="rounded-md bg-surface-container-high px-1.5 py-0.5 text-[10px] font-mono font-bold text-on-surface-variant uppercase">
                        {item.alias}
                      </span>
                    )}
                  </div>
                  {item.payslipDesc && item.payslipDesc !== item.name && (
                    <p className="text-[11px] text-on-surface-variant line-clamp-1 font-normal mt-0.5">
                      {item.payslipDesc}
                    </p>
                  )}
                </td>

                {/* Pay Head Type (Earning vs Deduction) */}
                <td className="w-36 px-4 py-3.5">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${
                      item.payHeadType === 'Earning'
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : 'bg-rose-500/10 text-rose-600'
                    }`}
                  >
                    <span className="material-symbols-outlined text-xs">
                      {item.payHeadType === 'Earning' ? 'add_circle' : 'remove_circle'}
                    </span>
                    <span>{item.payHeadType}</span>
                  </span>
                </td>

                {/* Calculation Type */}
                <td className="w-48 px-4 py-3.5 text-xs text-on-surface font-medium">
                  <span className="rounded-lg bg-surface-container-low px-2 py-1 border border-outline-variant/20">
                    {item.calculationType}
                  </span>
                </td>

                {/* Rules & Slabs Indicator */}
                <td className="w-40 px-4 py-3.5 text-xs text-on-surface-variant">
                  {item.calculationType === 'As Computed Value' ? (
                    item.slabCount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                        <span className="material-symbols-outlined text-xs">table_chart</span>
                        <span>{item.slabCount} Slabs</span>
                      </span>
                    ) : item.formulaCount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                        <span className="material-symbols-outlined text-xs">functions</span>
                        <span>{item.percentage}% of {item.formulaCount} Heads</span>
                      </span>
                    ) : (
                      <span className="text-[11px] italic text-on-surface-variant">Configured</span>
                    )
                  ) : (
                    <span className="text-[11px] text-on-surface-variant/60">—</span>
                  )}
                </td>

                {/* Include in Salary Slip */}
                <td className="w-28 px-4 py-3.5 text-center">
                  <span
                    className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold ${
                      item.includeInSalarySlip === 'Yes'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-surface-container-high text-on-surface-variant/60'
                    }`}
                  >
                    {item.includeInSalarySlip}
                  </span>
                </td>

                {/* Status */}
                <td className="w-28 px-4 py-3.5 text-center">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                      item.status === 'Active'
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : 'bg-surface-container-high text-on-surface-variant'
                    }`}
                  >
                    {item.status}
                  </span>
                </td>

                {/* Actions Column */}
                <td
                  className="w-16 px-5 py-3.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    items={[
                      { key: 'edit', label: 'Edit Pay Head', icon: 'edit' },
                      {
                        key: 'archive',
                        label: item.status === 'Active' ? 'Archive' : 'Restore',
                        icon: item.status === 'Active' ? 'archive' : 'unarchive',
                      },
                      { key: 'delete', label: 'Delete', icon: 'delete', danger: true },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        navigate(`/payroll/payheads/entry/${item.id}`);
                      } else if (key === 'archive') {
                        onArchive?.(item.id, item.name, item.status === 'Archive');
                      } else if (key === 'delete') {
                        onDelete?.(item.id, item.name);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
